import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { TCartItem } from 'store/api/cuverse-api/cart/types/cart-response.interface';
import { TMinerData } from 'store/api/cuverse-api/purchase/types/purchase-active-miners.interface';
import {
  createCartThunkAction,
  deleteAllCartItemsThunkAction,
  deleteCartItemThunkAction,
  updateAllCartThunkAction,
  updateCartThunkAction,
} from 'store/cart-reducer/cart.thunk-actions';
import { selectCart } from 'store/cart-reducer/cart-selectors';
import { selectInvoiceStatus } from 'store/invoice-reducer/invoice.selectors';
import { fetchInvoiceThunkAction } from 'store/invoice-reducer/invoice.thunk-actions';
import { closePopupAction } from 'store/modals-reducer/modals.reducer';
import { EPaymentCategory, setPaymentCategoryAction } from 'store/payment-reducer/payment.reducer';
import { selectProfile } from 'store/profile-reducer/profile.selectors';
import { requestProfileDataThunkAction } from 'store/profile-reducer/profile.thunk-actions';
import { EPromoCodeStatus } from 'store/promocode-reducer/constants/promocode-statuses';
import {
  clearPromoCheckState,
  setPromoStatusAction,
} from 'store/promocode-reducer/promocode.reducer';
import {
  selectActiveMiners,
  selectIsFirstPurchase,
  selectTrialMiners,
} from 'store/purchase-reducer/purchase.selectors';
import { useAppDispatch } from 'store/store';

import { mergeArraysById } from 'utils/common/arraysMergeByIdO1';
import { debounce } from 'utils/common/debounce';
import { removeNonNumericSymbols } from 'utils/formatters/remove-non-numeric-symbols.util';
import { gtmClickCart, gtmClickCartCheckout } from 'utils/gtmSender/gtmSender';
import { AppRoute } from 'utils/route/app-route';

import { CartContext } from './CartContext';

interface ICartContextProviderProps {
  children?: ReactNode;
}

export type TTransformedCartItem = Omit<TCartItem, 'productId'> & { id: number };

export const CartContextProvider: FC<ICartContextProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const cartFetchStatus = useSelector(selectInvoiceStatus);
  const minersData = useSelector(selectActiveMiners);
  const trialMinersData = useSelector(selectTrialMiners);
  const cartData = useSelector(selectCart);
  const { hasTrial } = useSelector(selectProfile);
  const isFirstPurchase = useSelector(selectIsFirstPurchase);
  const isCartPending = cartFetchStatus === 'pending';
  const { cartId, cartItems } = cartData;
  const cartItemsAllQuantity = cartItems.reduce((acc, item) => acc + item.quantity, 0);
  const isEmpty = cartItemsAllQuantity === 0;
  const isCartExists = !!cartId;

  /** Rerender state is used to force a re-render of values in the context */
  const [shouldRerender, setShouldRerender] = useState(false);

  const isInitialRender = useRef(true);

  /** Stores quantity of each item in the cart */
  const cartItemsQuantityMap = useMemo(
    () =>
      new Map<
        number,
        {
          maxQuantity: number;
          currentQuantity: number;
        }
      >(),
    [],
  );

  const gtmItems = minersData.map((minerData) => {
    const cartItem = cartItems.find((item) => item.productId === minerData.id);
    return {
      item_name: minerData.model,
      item_id: minerData.id,
      price: minerData.price,
      item_brand: minerData.model.split(' ')[0],
      item_hash_rate: minerData.hashrate,
      item_electricity_fee: minerData.dailyElectricityFee,
      quantity: cartItem ? cartItem.quantity : 0,
    };
  });

  /**
   * Applies GTM click event logic for a given minerData item by id.
   * If such item exists, calls gtmClickCart with it's data.
   * @param id - id of the minerData item to apply GTM click event logic.
   */
  const applyGmtEventLogic = (id: number) => {
    const minerDataById = minersData.find((el) => el.id === id);
    if (minerDataById) {
      const { model, id, price, hashrate, dailyElectricityFee } = minerDataById;
      gtmClickCart(model, id, price, model.split(' ')[0], hashrate, dailyElectricityFee);
    }
  };

  /**
   * Transform cartItems array to array with id property
   * */
  const transformCartListId = cartItems.map((el) => {
    const { productId, ...rest } = el;
    return {
      id: productId,
      ...rest,
    };
  });

  /**
   * Merge cartItems array with minersData array
   * */
  const mergeCartDataWithMinersData = mergeArraysById<TTransformedCartItem, TMinerData>(
    transformCartListId,
    minersData,
  );

  const cartListWithMinersData = mergeArraysById<TTransformedCartItem, TMinerData>(
    mergeCartDataWithMinersData,
    trialMinersData,
  );

  const DEBOUNCE_DELAY = 200;

  const trialMinersInCartLength = cartListWithMinersData.filter((item) =>
    item.name.toLowerCase().includes('trial'),
  ).length;

  const minersInCartLength = cartListWithMinersData.filter(
    (item) => !item.name.toLowerCase().includes('trial'),
  ).length;

  /** Checks if the cart contains only trial miners */
  const isTrialMinerCheckout = minersInCartLength <= 0 && trialMinersInCartLength > 0;

  /**
   * Debounced patch request
   * */
  const debouncedPatchRequest = useRef(
    debounce(
      async ({
        minerId,
        quantity,
        cartId,
      }: {
        minerId: number;
        quantity: number;
        cartId: number;
      }) => {
        applyGmtEventLogic(minerId);
        await dispatch(updateCartThunkAction({ cartId, id: minerId, quantity }));
      },
      DEBOUNCE_DELAY,
    ),
  );

  /**
   * Debounced create request
   * */
  const debouncedCreateRequest = useRef(
    debounce(
      async ({ minerId, quantity }: { minerId: number; quantity: number }): Promise<void> => {
        applyGmtEventLogic(minerId);
        await dispatch(createCartThunkAction({ id: minerId, quantity }));
      },
      DEBOUNCE_DELAY,
    ),
  );

  /**
   * Debounced delete request
   * */
  const debouncedDeleteRequest = useRef(
    debounce(({ minerId, cartId }: { minerId: number; cartId: number }) => {
      applyGmtEventLogic(minerId);
      void dispatch(deleteCartItemThunkAction({ id: minerId, cartId }));
    }, DEBOUNCE_DELAY),
  );

  /**
   * Debounced update all request
   * */
  const debouncedUpdateAllRequest = useRef(
    debounce(
      ({
        cartId,
        body,
        minerId,
      }: {
        cartId: number;
        minerId: number;
        body: {
          cart_items: {
            quantity: number;
            product_id: number;
          }[];
        };
      }) => {
        applyGmtEventLogic(minerId);
        void dispatch(updateAllCartThunkAction({ cartId, body }));
      },
      DEBOUNCE_DELAY,
    ),
  );

  /**
   * Adds a trial miner to the cart.
   * If the cart does not exist yet, creates a new one with the given id and quantity 1.
   * If the cart already exists, updates the quantity of the item with the given id to 1.
   * @param {number} id the id of the trial miner to add to the cart
   */
  const handleAddTrialMinerToCart = async (id: number) => {
    try {
      if (!isCartExists) {
        return await dispatch(createCartThunkAction({ id, quantity: 1 })).unwrap();
      } else {
        return await dispatch(updateCartThunkAction({ cartId, id, quantity: 1 })).unwrap();
      }
    } catch (error) {
      return { cartId: null };
    }
  };

  /**
   * Toggles the tooltip's open state.
   * If the tooltip is currently open, it will be closed, and vice versa.
   */
  const handleTooltipOpen = () => {
    setIsTooltipOpen(!isTooltipOpen);
  };

  /**
   * Closes the tooltip by setting isTooltipOpen to false.
   */
  const handleTooltipClose = () => {
    setIsTooltipOpen(false);
  };

  /**
   * Increments the quantity of the item with the given id in the cart.
   * If the item is not in the cart, creates a new item with the given id and quantity 1.
   * If the item is already in the cart and the quantity is equal to the maximum quantity, does nothing.
   * Sets the shouldRerender state to true.
   * @param {number} id the id of the item to increment
   */
  const handleIncrement = async (id: number) => {
    const isTrialMiner =
      minersData
        .concat(trialMinersData)
        .find((miner) => miner.model.toLowerCase().includes('trial'))?.id === id;

    const isTrialInCart = cartItems.find((item) => item.name.toLowerCase().includes('trial'));

    if (isTrialMiner && isTrialInCart) return;

    const currentItemMap = cartItemsQuantityMap.get(id) ?? {
      maxQuantity: 999,
      currentQuantity: 0,
    };

    if (currentItemMap.currentQuantity >= currentItemMap.maxQuantity) {
      return;
    }

    cartItemsQuantityMap.set(id, {
      ...currentItemMap,
      currentQuantity: currentItemMap.currentQuantity + 1,
    });

    const updatedItemMap = cartItemsQuantityMap.get(id);

    if (!isCartExists) {
      debouncedCreateRequest.current({
        minerId: id,
        quantity: updatedItemMap?.currentQuantity ?? 1,
      });
      setShouldRerender(!shouldRerender);
      return;
    }

    debouncedPatchRequest.current({
      quantity: updatedItemMap?.currentQuantity ?? 1,
      cartId,
      minerId: id,
    });

    setShouldRerender(!shouldRerender);
  };

  /**
   * Decrements the quantity of the item with the given id in the cart.
   * If the quantity reaches 0, the item is removed from the cart.
   * If the item is not in the cart, does nothing.
   * Sets the shouldRerender state to true.
   * @param {number} id the id of the item to decrement
   */
  const handleDecrement = (id: number) => {
    const currentItemMap = cartItemsQuantityMap.get(id) ?? {
      maxQuantity: 999,
      currentQuantity: 0,
    };

    cartItemsQuantityMap.set(id, {
      ...currentItemMap,
      currentQuantity: currentItemMap.currentQuantity - 1,
    });

    const updatedItemMap = cartItemsQuantityMap.get(id);

    if (updatedItemMap && updatedItemMap.currentQuantity < 0) {
      cartItemsQuantityMap.delete(id);
      setShouldRerender(!shouldRerender);
      return;
    }

    if (updatedItemMap?.currentQuantity === 0) {
      cartItemsQuantityMap.delete(id);
      void debouncedDeleteRequest.current({ minerId: id, cartId });
      setShouldRerender(!shouldRerender);
      return;
    }

    void debouncedPatchRequest.current({
      quantity: updatedItemMap?.currentQuantity ?? 0,
      cartId,
      minerId: id,
    });

    setShouldRerender(!shouldRerender);
  };

  /**
   * Handles the input change in the quantity input field.
   * Will update the cart items quantity map and set the shouldRerender flag.
   * If the input value is less than or equal to 0, the item will be removed from the cart.
   * If isTrialMiner is true, the quantity will be set to 1.
   * @param e The change event of the input field.
   * @param id The id of the item to update.
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    e.preventDefault();
    const enterValue = parseInt(removeNonNumericSymbols(e.target.value));
    const value = isNaN(enterValue) ? 0 : enterValue;

    const currentCartItem = cartItems.find((item) => item.productId === id);

    const isTrialMiner = currentCartItem?.name.toLowerCase().includes('trial');

    if (!currentCartItem) return;

    cartItemsQuantityMap.set(id, {
      maxQuantity: currentCartItem.maxQuantity,
      currentQuantity: Math.min(isTrialMiner ? 1 : currentCartItem.maxQuantity, value),
    });

    if (value <= 0) {
      cartItemsQuantityMap.delete(id);
    }

    setShouldRerender(!shouldRerender);
  };

  /**
   * This function will be called when the input field is blurred or the Enter key is pressed in the input field.
   * If the entered quantity is 0, the item will be removed from the cart.
   * If the entered quantity is greater than the maximum quantity, the quantity will be set to the maximum quantity.
   * The function will call the debouncedUpdateAllRequest function with the updated quantity.
   * @param id id of the item to update
   */
  const applyInputLogic = (id: number) => {
    const currentItemMap = cartItemsQuantityMap.get(id);

    if (!currentItemMap || currentItemMap.currentQuantity === 0) {
      const filteredCartItems = cartItems.filter((item) => item.productId !== id);

      const updatedCartItems = filteredCartItems.map((item) => ({
        product_id: item.productId,
        quantity: item.quantity,
      }));

      void debouncedUpdateAllRequest.current({
        cartId,
        minerId: id,
        body: { cart_items: updatedCartItems },
      });

      setShouldRerender(!shouldRerender);
      return;
    }

    const cartListItem = cartItems.find((item) => item.productId === id);

    const updatedCartItems = cartItems.map((item) => {
      const { productId, quantity } = item;

      if (productId === id)
        return {
          quantity: Math.min(cartListItem?.maxQuantity || 0, currentItemMap.currentQuantity),
          product_id: item.productId,
        };
      return { product_id: productId, quantity };
    });

    void debouncedUpdateAllRequest.current({
      cartId,
      minerId: id,
      body: { cart_items: updatedCartItems },
    });
    setShouldRerender(!shouldRerender);
  };

  /**
   * This function will be called when the Enter key is pressed in the input field.
   * It will call the applyInputLogic function with the id of the item as an argument.
   * @param e event object
   * @param id id of the item to update
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, id: number) => {
    if (e.key !== 'Enter') return;
    applyInputLogic(id);
  };

  /**
   * This function will be called when the input field is blurred.
   * It will call the applyInputLogic function with the id of the item as an argument.
   * @param id id of the item to update
   */
  const handleBlur = (id: number) => {
    applyInputLogic(id);
  };

  /**
   * Delete all items from cart with the same id.
   * This function will be called when the user click on the "Delete all" button.
   * It will dispatch the updateAllCartThunkAction and delete the item from the cartItemsQuantityMap.
   * This function won't do anything if the cart is currently pending.
   * @param id id of the item to delete
   */
  const handleDeleteAllSameItems = (id: number) => {
    if (isCartPending) return;
    const updatedCartItems = cartItems
      .filter((item) => item.productId !== id)
      .map((item) => {
        const { productId, quantity } = item;

        if (productId === id)
          return {
            quantity: 0,
            product_id: item.productId,
          };
        return { product_id: productId, quantity };
      });

    void dispatch(updateAllCartThunkAction({ cartId, body: { cart_items: updatedCartItems } }));

    cartItemsQuantityMap.delete(id);
  };

  /**
   * Delete all items from cart.
   * This function will be called when the user click on the "Clear cart" button.
   * It will dispatch the deleteAllCartItemsThunkAction and clear the cartItemsQuantityMap.
   * This function won't do anything if the cart is currently pending.
   */
  const handleDeleteAllItems = () => {
    if (!isCartPending) {
      void dispatch(deleteAllCartItemsThunkAction({ cartId }));
      cartItemsQuantityMap.clear();
    }
  };

  /**
   * Handle checkout button click.
   * If checkout page is opened, closes the tooltip.
   * Otherwise, checks if user has filled in his profile data.
   * If not, navigates to the PersonalInfo page and closes the tooltip.
   * If yes, sends a checkout click event to Google Tag Manager,
   * fetches the invoice, sets the promo status to "Button",
   * navigates to the Checkout page and closes the tooltip.
   */
  const handleCheckoutClick = async () => {
    if (location.pathname === AppRoute.PurchaseCheckout()) {
      setIsTooltipOpen(false);
      return;
    }

    const profileData = await dispatch(requestProfileDataThunkAction()).unwrap();
    const { firstname, lastname, country, city, zip, address1, state } = profileData;

    if (!firstname || !lastname || !country || !city || !zip || !address1 || !state) {
      navigate(AppRoute.PurchasePersonalInfo(), { replace: true });
      setIsTooltipOpen(false);
      dispatch(closePopupAction());
    } else {
      const itemsInCart = gtmItems.filter((el) => el.quantity);
      gtmClickCartCheckout(itemsInCart);
      await dispatch(fetchInvoiceThunkAction(cartId));
      dispatch(clearPromoCheckState());

      navigate(AppRoute.PurchaseCheckout(), { replace: true });
      dispatch(closePopupAction());
      setIsTooltipOpen(false);
    }
  };

  /**
   * Initiates the trial checkout process.
   * Checks if a trial miner is already in the cart or needs to be added.
   * If there's no trial miner in the cart, adds it and proceeds with the checkout.
   * If the user is on the Payment page, closes the tooltip.
   * Dispatches actions to update payment category and fetch invoice details.
   * Navigates to the Payment page if only trial miners are in the cart.
   * Otherwise, navigates to the Purchase Checkout page.
   * Closes any open popups and tooltips.
   */
  const handleTrialCheckoutClick = async () => {
    if (hasTrial) return;

    if (location.pathname === AppRoute.Payment()) {
      setIsTooltipOpen(false);
      return;
    }

    const trialMinerId = trialMinersData.find((miner) =>
      miner.model.toLowerCase().includes('trial'),
    )?.id;

    if (!trialMinerId) return;

    const trialMinerInCart = cartItems.find((item) => item.name.toLowerCase().includes('trial'));

    dispatch(setPaymentCategoryAction(EPaymentCategory.Cart));

    /**
     * Executes the trial checkout process logic.
     * Fetches the invoice for the given cart id,
     * checks if only trial miners are in the cart,
     * navigates to either the Payment page (if only trial miners are in the cart)
     * or the Purchase Checkout page (if not),
     * closes any open popups and tooltips.
     * @param {number} cartId - id of the cart
     */
    const executeTrialCheckoutLogic = async (cartId: number) => {
      if (minersInCartLength > 0) {
        const profileData = await dispatch(requestProfileDataThunkAction()).unwrap();
        const { firstname, lastname, country, city, zip, address1, state } = profileData;

        if (!firstname || !lastname || !country || !city || !zip || !address1 || !state) {
          navigate(AppRoute.PurchasePersonalInfo(), { replace: true });
          setIsTooltipOpen(false);
          dispatch(closePopupAction());
          return;
        }
      }

      await dispatch(fetchInvoiceThunkAction(cartId));
      const onlyTrialMinersInCart =
        cartItems.filter((item) => !item.name.toLowerCase().includes('trial')).length === 0;

      const currentItemMap = cartItemsQuantityMap.get(trialMinerId) ?? {
        maxQuantity: 1,
        currentQuantity: 0,
      };

      cartItemsQuantityMap.set(trialMinerId, {
        ...currentItemMap,
        currentQuantity: 1,
      });

      if (onlyTrialMinersInCart) {
        navigate(AppRoute.Payment());
      } else {
        dispatch(setPromoStatusAction(EPromoCodeStatus.Button));
        navigate(AppRoute.PurchaseCheckout(), { replace: true });
      }

      dispatch(closePopupAction());
      setIsTooltipOpen(false);
    };

    if (trialMinerInCart) {
      await executeTrialCheckoutLogic(cartId);
      return;
    }

    const { cartId: actualCartId } = await handleAddTrialMinerToCart(trialMinerId);
    if (!actualCartId) return;
    void executeTrialCheckoutLogic(actualCartId);
  };

  /**
   * Checks if an item with the given id is in the cart.
   * @param id id of the item to check
   * @returns true if the item is in the cart, false otherwise
   */
  const handleItemInCart = (id: number) => {
    return cartItems.some((item) => item.productId === id);
  };

  /**
   * Clears all entries from the cartItemsQuantityMap.
   * This function should be called when there is a need to reset the item quantities in the cart,
   * effectively removing all items from the map.
   */
  const handleItemsMapClear = () => {
    cartItemsQuantityMap.clear();
  };

  /**
   * This hook will be called when the component mounts.
   * It will update the cartItemsQuantityMap with the current cart items.
   * It will also set the isInitialRender to false if the cartItemsQuantityMap is not empty.
   */
  useEffect(() => {
    if (isInitialRender.current) {
      cartItems.forEach((item) => {
        cartItemsQuantityMap.set(item.productId, {
          maxQuantity: item.maxQuantity,
          currentQuantity: item.quantity,
        });
      });

      if (cartItems.length > 0) {
        isInitialRender.current = false;
      }
    }
  }, [
    cartItems,
    cartItems.length,
    cartItemsQuantityMap,
    cartItemsQuantityMap.size,
    shouldRerender,
  ]);

  return (
    <CartContext.Provider
      value={{
        cartId,
        cartItems,
        minersData,
        isEmpty,
        cartItemsAllQuantity,
        isTooltipOpen,
        cartListWithMinersData,
        isCartPending,
        cartItemsQuantityMap,
        minersInCartLength,
        isTrialMinerCheckout,
        trialMinersInCartLength,
        handleTooltipOpen,
        handleTooltipClose,
        handleIncrement,
        handleDecrement,
        handleDeleteAllSameItems,
        handleDeleteAllItems,
        handleCheckoutClick,
        handleTrialCheckoutClick,
        setIsTooltipOpen,
        handleItemInCart,
        handleInputChange,
        handleKeyDown,
        handleBlur,
        handleItemsMapClear,
        t,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
